import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Download, 
  Share2, 
  MessageSquare, 
  Send, 
  Heart, 
  Sparkles, 
  Copy, 
  Check, 
  Mail, 
  Palette,
  PartyPopper
} from 'lucide-react';
import { 
  renderCelebrationCardCanvas, 
  downloadCanvasAsImage 
} from './CelebrationCanvasExporter';
import { 
  CELEBRATION_STICKERS, 
  OCCASION_QUOTES, 
  patchCelebrationData,
  fetchGreetingsForCelebrant
} from './celebrationsData';
import { fireConfetti } from './confettiUtil';

const config = require('../../../config.json');

export default function CelebrationWorkspaceModal({ celebrant, isOpen, onClose, initialTab = 'ecard', onUpdateCelebrant }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [theme, setTheme] = useState('royal'); // 'royal' | 'gold' | 'celestial' | 'festive'
  const [customWish, setCustomWish] = useState('');
  const [senderName, setSenderName] = useState('Vidyajyoti College Community');
  const [senderRole, setSenderRole] = useState('Student / Staff / Well-wisher');
  const [copied, setCopied] = useState(false);

  // Guestbook Greetings state - fetched only from backend API
  const [greetingsList, setGreetingsList] = useState([]);
  const [newMsgName, setNewMsgName] = useState('');
  const [newMsgRole, setNewMsgRole] = useState('');
  const [newMsgContent, setNewMsgContent] = useState('');
  const [selectedSticker, setSelectedSticker] = useState('🎂');

  // Reaction counts state - fetched only from backend API
  const [counts, setCounts] = useState({
    loveCount: 0,
    cakeCount: 0,
    blessingCount: 0,
    cheerCount: 0
  });
  const [userReactions, setUserReactions] = useState({
    love: false,
    cake: false,
    blessing: false,
    cheer: false
  });

  const canvasRef = useRef(null);
  const celebrantId = celebrant?.id || celebrant?.registrationNo || '';
  const isInitializedRef = useRef(false);

  // Sync state once when modal opens or celebrantId changes
  useEffect(() => {
    if (celebrant && isOpen) {
      setActiveTab(initialTab);
      setCustomWish(celebrant.quote || '');
      
      const initialG = Array.isArray(celebrant.greetings) 
        ? celebrant.greetings 
        : (Array.isArray(celebrant.initialGreetings) ? celebrant.initialGreetings : []);
      setGreetingsList(initialG);

      const initialCounts = {
        loveCount: Number(celebrant.loveCount || 0),
        cakeCount: Number(celebrant.cakeCount || 0),
        blessingCount: Number(celebrant.blessingCount || 0),
        cheerCount: Number(celebrant.cheerCount || 0)
      };
      setCounts(initialCounts);

      setUserReactions({
        love: false,
        cake: false,
        blessing: false,
        cheer: false
      });

      // Fetch once per open if needed
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
        fetchGreetingsForCelebrant(celebrantId, config).then((data) => {
          if (data && Array.isArray(data.greetings) && data.greetings.length > 0) {
            setGreetingsList(data.greetings);
            setCounts({
              loveCount: Number(data.loveCount ?? initialCounts.loveCount),
              cakeCount: Number(data.cakeCount ?? initialCounts.cakeCount),
              blessingCount: Number(data.blessingCount ?? initialCounts.blessingCount),
              cheerCount: Number(data.cheerCount ?? initialCounts.cheerCount)
            });
          }
        });
      }

      // Fire celebratory confetti when opening modal
      fireConfetti({ particleCount: 50, spread: 60 });
    } else {
      isInitializedRef.current = false;
    }
  }, [isOpen, celebrantId, initialTab]);

  // Re-render canvas whenever relevant fields or tab change
  useEffect(() => {
    if (isOpen && celebrant && activeTab === 'ecard' && canvasRef.current) {
      renderCelebrationCardCanvas(canvasRef.current, {
        celebrant,
        theme,
        customWish,
        senderName,
        senderRole
      });
    }
  }, [isOpen, celebrant, activeTab, theme, customWish, senderName, senderRole]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !celebrant) return null;

  const handleDownloadImage = () => {
    if (canvasRef.current) {
      const cleanName = (celebrant.name || 'celebrant').replace(/[^a-zA-Z0-9]/g, '_');
      downloadCanvasAsImage(canvasRef.current, `${cleanName}_Vidyajyoti_${celebrant.occasionType}_card.png`);
      fireConfetti({ particleCount: 80, spread: 80 });
    }
  };

  const handleCopyLink = () => {
    const shareText = `🎉 Celebrating ${celebrant.name}'s ${celebrant.occasionTitle} at Vidyajyoti College of Theology!\n\n"${customWish || celebrant.quote}"\n\n- With warm wishes from ${senderName}`;
    navigator.clipboard.writeText(shareText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleSendGreeting = (e) => {
    e.preventDefault();
    if (!newMsgName.trim() || !newMsgContent.trim()) return;

    const newGreeting = {
      id: `g-${Date.now()}`,
      senderName: newMsgName.trim(),
      senderRole: newMsgRole.trim() || 'Well-wisher',
      message: newMsgContent.trim(),
      sticker: selectedSticker,
      timestamp: 'Just now',
      createdAt: new Date().toISOString()
    };

    const updated = [newGreeting, ...greetingsList];
    setGreetingsList(updated);
    setNewMsgContent('');

    // Immediately update parent state so CelebrationCard displays updated wishes count
    if (onUpdateCelebrant) {
      onUpdateCelebrant(celebrant.id || celebrant.registrationNo, {
        ...counts,
        greetings: updated,
        totalGreetings: updated.length
      });
    }

    // Dynamic backend sync with DynamoDB via PATCH /website/celebration
    patchCelebrationData(celebrant, counts, updated, config);

    // Trigger celebratory burst!
    fireConfetti({ particleCount: 90, spread: 90 });
  };

  const handleReactionClick = (reactionType, e) => {
    const field = `${reactionType}Count`;
    const updatedCounts = {
      ...counts,
      [field]: (Number(counts[field]) || 0) + 1
    };
    setCounts(updatedCounts);
    setUserReactions(prev => ({ ...prev, [reactionType]: true }));

    // Immediately update parent state so CelebrationCard displays updated reaction count
    if (onUpdateCelebrant) {
      onUpdateCelebrant(celebrant.id || celebrant.registrationNo, {
        ...updatedCounts,
        greetings: greetingsList,
        totalGreetings: greetingsList.length
      });
    }

    // Dynamic backend sync with DynamoDB via PATCH /website/celebration
    patchCelebrationData(celebrant, updatedCounts, greetingsList, config);

    // Small local confetti from click position
    if (e && e.clientX) {
      fireConfetti({
        particleCount: 35,
        origin: { x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight }
      });
    }
  };

  // WhatsApp share link
  const waMessage = encodeURIComponent(
    `🌟 *Vidyajyoti College Celebrations* 🌟\n\nDear ${celebrant.name},\n\nWishing you a joyful *${celebrant.occasionTitle}*!\n\n"${customWish || celebrant.quote}"\n\n— With warm prayers and best wishes from *${senderName}* (${senderRole})`
  );
  const waUrl = celebrant.phone 
    ? `https://wa.me/${celebrant.phone.replace(/[^0-9]/g, '')}?text=${waMessage}` 
    : `https://api.whatsapp.com/send?text=${waMessage}`;

  // Email share link
  const emailSubject = encodeURIComponent(`${celebrant.occasionTitle} Greetings - Vidyajyoti College`);
  const emailBody = encodeURIComponent(
    `Dear ${celebrant.name},\n\nHeartiest greetings on your ${celebrant.occasionTitle}!\n\n"${customWish || celebrant.quote}"\n\nWith prayers and warmest regards,\n${senderName} (${senderRole})\nVidyajyoti College of Theology`
  );
  const mailtoUrl = `mailto:${celebrant.email || ''}?subject=${emailSubject}&body=${emailBody}`;

  const sampleQuotes = OCCASION_QUOTES[celebrant.occasionType] || OCCASION_QUOTES.birthday;

  const totalReactionsCount = (Number(counts.loveCount) || 0) + 
    (Number(counts.cakeCount) || 0) + 
    (Number(counts.blessingCount) || 0) + 
    (Number(counts.cheerCount) || 0);

  return (
    <div className="vj-modal-overlay" onClick={onClose}>
      <div className="vj-workspace-dialog" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="vj-modal-header">
          <div className="vj-modal-title-wrap">
            <div className="vj-icon-badge" style={{ background: 'rgba(255,255,255,0.2)', color: '#fbbf24' }}>
              <PartyPopper size={22} />
            </div>
            <div>
              <h4 className="fw-bold mb-0 text-white" style={{ fontSize: '1.25rem' }}>
                Celebration Workspace & E-Card Studio
              </h4>
              <span className="text-white opacity-80" style={{ fontSize: '0.85rem' }}>
                Honoring <strong>{celebrant.name}</strong> • {celebrant.occasionTitle}
              </span>
            </div>
          </div>
          <button className="vj-modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="vj-workspace-tabs">
          <button 
            className={`vj-tab-btn ${activeTab === 'ecard' ? 'active' : ''}`}
            onClick={() => setActiveTab('ecard')}
          >
            <Palette size={18} /> 🎨 Digital E-Card Studio & Download
          </button>
          <button 
            className={`vj-tab-btn ${activeTab === 'guestbook' ? 'active' : ''}`}
            onClick={() => setActiveTab('guestbook')}
          >
            <MessageSquare size={18} /> 💌 Community Greetings Wall ({greetingsList.length})
          </button>
          <button 
            className={`vj-tab-btn ${activeTab === 'reactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('reactions')}
          >
            <Heart size={18} /> ❤️ Reactions ({totalReactionsCount})
          </button>
        </div>

        {/* Modal Body Content */}
        <div className="vj-workspace-body">
          {activeTab === 'ecard' && (
            <div className="row g-4">
              {/* Left Column: Live Card Canvas Preview */}
              <div className="col-lg-7 col-md-12">
                <div className="vj-card-preview-box">
                  <canvas 
                    ref={canvasRef} 
                    className="vj-card-preview-canvas"
                  />
                  <div className="d-flex align-items-center justify-content-between w-100 mt-2.5 px-2 text-white opacity-75" style={{ fontSize: '0.78rem' }}>
                    <span>High-Resolution 1200x800 e-Card</span>
                    <span>Ready for WhatsApp, Status & Print</span>
                  </div>
                </div>

                {/* Export & Sharing Action Bar */}
                <div className="vj-export-actions">
                  <button className="vj-btn-download-card" onClick={handleDownloadImage}>
                    <Download size={18} /> Download E-Card (PNG)
                  </button>
                  <a href={waUrl} target="_blank" rel="noreferrer" className="vj-btn-whatsapp-share">
                    <Share2 size={18} /> Share via WhatsApp
                  </a>
                  {celebrant.email && (
                    <a href={mailtoUrl} className="vj-btn-email-share">
                      <Mail size={18} /> Send Email
                    </a>
                  )}
                  <button className="vj-btn-copy-card" onClick={handleCopyLink}>
                    {copied ? <Check size={18} className="text-success" /> : <Copy size={18} />}
                    {copied ? 'Copied to Clipboard!' : 'Copy Greeting'}
                  </button>
                </div>
              </div>

              {/* Right Column: Customizer Controls */}
              <div className="col-lg-5 col-md-12">
                {/* Theme Selector */}
                <div className="mb-3">
                  <label className="vj-control-label">Card Theme Style</label>
                  <div className="vj-theme-picker">
                    <button 
                      className={`vj-theme-btn ${theme === 'royal' ? 'active' : ''}`}
                      onClick={() => setTheme('royal')}
                    >
                      👑 Royal Jesuit
                    </button>
                    <button 
                      className={`vj-theme-btn ${theme === 'gold' ? 'active' : ''}`}
                      onClick={() => setTheme('gold')}
                    >
                      ✨ Golden Radiance
                    </button>
                    <button 
                      className={`vj-theme-btn ${theme === 'celestial' ? 'active' : ''}`}
                      onClick={() => setTheme('celestial')}
                    >
                      🌌 Sapphire Night
                    </button>
                    <button 
                      className={`vj-theme-btn ${theme === 'festive' ? 'active' : ''}`}
                      onClick={() => setTheme('festive')}
                    >
                      🎉 Festive Ivory
                    </button>
                  </div>
                </div>

                {/* Preset Quotes Selection */}
                <div className="mb-3">
                  <label className="vj-control-label">Pick a Preset Blessing / Greeting</label>
                  <div className="vj-quick-wishes-chips">
                    {sampleQuotes.slice(0, 3).map((q, idx) => (
                      <button 
                        key={idx} 
                        className="vj-wish-chip-btn"
                        onClick={() => setCustomWish(q)}
                      >
                        “{q.length > 75 ? q.substring(0, 75) + '...' : q}”
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Message Editor */}
                <div className="mb-3">
                  <label className="vj-control-label">Or Write Your Custom Message</label>
                  <textarea 
                    rows={3}
                    className="vj-custom-textarea"
                    placeholder="Write a personalized heartfelt wish..."
                    value={customWish}
                    onChange={(e) => setCustomWish(e.target.value)}
                  />
                </div>

                {/* Sender Name & Role */}
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="vj-control-label">Your Name</label>
                    <input 
                      type="text" 
                      className="vj-custom-input" 
                      placeholder="e.g. Br. Thomas SJ"
                      value={senderName}
                      onChange={(e) => setSenderName(e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <label className="vj-control-label">Your Batch / Role</label>
                    <input 
                      type="text" 
                      className="vj-custom-input" 
                      placeholder="e.g. B.Th I Year"
                      value={senderRole}
                      onChange={(e) => setSenderRole(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'guestbook' && (
            <div className="vj-greetings-wall-container">
              {/* Form to Post New Wish */}
              <div className="vj-new-greeting-card">
                <h6 className="fw-bold text-dark-main mb-2 d-flex align-items-center gap-2">
                  <Sparkles size={18} className="text-gold" /> Send a Greeting to {celebrant.name}
                </h6>
                <form onSubmit={handleSendGreeting}>
                  <div className="row g-2 mb-2">
                    <div className="col-md-6 col-12">
                      <input 
                        type="text" 
                        required
                        className="vj-custom-input" 
                        placeholder="Your Name (e.g. Sr. Philo, Fr. Alex, John)"
                        value={newMsgName}
                        onChange={(e) => setNewMsgName(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 col-12">
                      <input 
                        type="text" 
                        className="vj-custom-input" 
                        placeholder="Your Relationship / Batch (e.g. Classmate, Faculty, Alumnus)"
                        value={newMsgRole}
                        onChange={(e) => setNewMsgRole(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mb-2">
                    <label className="vj-control-label mb-1">Pick a Celebration Sticker</label>
                    <div className="vj-sticker-selector">
                      {CELEBRATION_STICKERS.map((stk) => (
                        <button 
                          key={stk.id}
                          type="button"
                          className={`vj-sticker-option ${selectedSticker === stk.emoji ? 'selected' : ''}`}
                          onClick={() => setSelectedSticker(stk.emoji)}
                        >
                          <span>{stk.emoji}</span> {stk.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-2">
                    <textarea 
                      rows={2} 
                      required
                      className="vj-custom-textarea" 
                      placeholder="Type your warm greetings and blessings here..."
                      value={newMsgContent}
                      onChange={(e) => setNewMsgContent(e.target.value)}
                    />
                  </div>

                  <div className="d-flex justify-content-end">
                    <button type="submit" className="vj-btn-open-card" style={{ padding: '0.6rem 1.4rem' }}>
                      <Send size={16} /> Post Greeting on Wall 🎉
                    </button>
                  </div>
                </form>
              </div>

              {/* Feed of Greetings */}
              <div>
                <h6 className="fw-bold text-dark mb-3">
                  Messages & Prayers Wall ({greetingsList.length})
                </h6>
                <div className="vj-greetings-feed">
                  {greetingsList.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      Be the first to post a warm greeting for {celebrant.name}! 🌟
                    </div>
                  ) : (
                    greetingsList.map((g) => (
                      <div key={g.id || `${g.senderName}-${g.createdAt}`} className="vj-greeting-bubble">
                        <div className="vj-greeting-bubble-top">
                          <div>
                            <span className="vj-greeting-sticker-badge">{g.sticker || '🎂'}</span>
                            <span className="vj-greeting-sender">{g.senderName}</span>
                            {g.senderRole && (
                              <span className="vj-greeting-sender-role">({g.senderRole})</span>
                            )}
                          </div>
                          <span className="vj-greeting-time">{g.timestamp}</span>
                        </div>
                        <p className="vj-greeting-text">{g.message}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'reactions' && (
            <div className="text-center py-4">
              <div className="vj-icon-badge mx-auto mb-3" style={{ width: '64px', height: '64px', fontSize: '2rem' }}>
                ❤️
              </div>
              <h5 className="fw-bold text-dark-main">Send Instant Blessings & Love to {celebrant.name}</h5>
              <p className="text-muted" style={{ maxWidth: '520px', margin: '0 auto 2rem' }}>
                Tap any reaction button below to shower love, prayers, and celebration for this joyous occasion.
              </p>

              <div className="d-flex justify-content-center gap-3 flex-wrap mb-4">
                <button 
                  className={`btn p-3 rounded-4 d-flex flex-column align-items-center gap-1 shadow-sm border ${
                    userReactions.love ? 'bg-danger text-white border-danger' : 'bg-white text-dark'
                  }`}
                  style={{ minWidth: '115px', cursor: 'pointer', transform: userReactions.love ? 'scale(1.03)' : 'scale(1)', transition: 'all 0.2s ease' }}
                  onClick={(e) => handleReactionClick('love', e)}
                  title="Click to send Love (+1)"
                >
                  <span style={{ fontSize: '2.2rem' }}>❤️</span>
                  <span className="fw-bold">Love</span>
                  <span className="badge bg-light text-dark rounded-pill px-2.5 py-1">
                    {counts.loveCount || 0}
                  </span>
                </button>

                <button 
                  className={`btn p-3 rounded-4 d-flex flex-column align-items-center gap-1 shadow-sm border ${
                    userReactions.cake ? 'bg-warning text-dark border-warning' : 'bg-white text-dark'
                  }`}
                  style={{ minWidth: '115px', cursor: 'pointer', transform: userReactions.cake ? 'scale(1.03)' : 'scale(1)', transition: 'all 0.2s ease' }}
                  onClick={(e) => handleReactionClick('cake', e)}
                  title="Click to celebrate (+1)"
                >
                  <span style={{ fontSize: '2.2rem' }}>🎂</span>
                  <span className="fw-bold">Celebration</span>
                  <span className="badge bg-light text-dark rounded-pill px-2.5 py-1">
                    {counts.cakeCount || 0}
                  </span>
                </button>

                <button 
                  className={`btn p-3 rounded-4 d-flex flex-column align-items-center gap-1 shadow-sm border ${
                    userReactions.blessing ? 'bg-primary text-white border-primary' : 'bg-white text-dark'
                  }`}
                  style={{ minWidth: '115px', cursor: 'pointer', transform: userReactions.blessing ? 'scale(1.03)' : 'scale(1)', transition: 'all 0.2s ease' }}
                  onClick={(e) => handleReactionClick('blessing', e)}
                  title="Click to send Prayers & Blessings (+1)"
                >
                  <span style={{ fontSize: '2.2rem' }}>🙏</span>
                  <span className="fw-bold">Prayers</span>
                  <span className="badge bg-light text-dark rounded-pill px-2.5 py-1">
                    {counts.blessingCount || 0}
                  </span>
                </button>

                <button 
                  className={`btn p-3 rounded-4 d-flex flex-column align-items-center gap-1 shadow-sm border ${
                    userReactions.cheer ? 'bg-success text-white border-success' : 'bg-white text-dark'
                  }`}
                  style={{ minWidth: '115px', cursor: 'pointer', transform: userReactions.cheer ? 'scale(1.03)' : 'scale(1)', transition: 'all 0.2s ease' }}
                  onClick={(e) => handleReactionClick('cheer', e)}
                  title="Click to Cheer (+1)"
                >
                  <span style={{ fontSize: '2.2rem' }}>👏</span>
                  <span className="fw-bold">Cheers</span>
                  <span className="badge bg-light text-dark rounded-pill px-2.5 py-1">
                    {counts.cheerCount || 0}
                  </span>
                </button>
              </div>

              <button 
                className="vj-btn-open-card px-4 py-2.5"
                onClick={() => fireConfetti({ particleCount: 120, spread: 100 })}
              >
                <PartyPopper size={18} /> Shower Confetti Burst! 🎊
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
